import React from "react";
import Layout from "../components/layout/Layout";
import Person1 from "../assets/person1.png";
import CTASection from "../components/common/CTASection";
import stay from "../assets/1.jpg";
import {
  ChevronDown,
  ChevronRight,
  Check,
  FileText,
  Leaf,
  TrendingUp,
  Eye,
  Lightbulb,
  Target,
  Award,
  Heart,
  Shield,
  Star,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

const AboutPage = () => {
  const [openSection, setOpenSection] = useState(null);

  return (
    <Layout>
      {/* ===== HERO SECTION ===== */}
      <section
        className="relative py-16 sm:py-20 bg-fixed bg-cover bg-center"
        style={{
          backgroundImage: `url(${stay})`,
          backgroundAttachment: "fixed",
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-[#2F3C7E]/70"></div>

        {/* Content */}
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">
            About Us
          </h1>
          <p className="text-lg sm:text-xl text-[#E6B325]">
            "Excellence, Timeliness, and Cleanliness"
          </p>
        </div>
      </section>

      {/* ===== DIRECTOR SECTION ===== */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center justify-center gap-8 sm:gap-10 lg:gap-12">
            {/* Left Column - Image with Name below */}
            <div className="w-full sm:w-8/12 md:w-6/12 lg:w-3/12 mb-6 lg:mb-0">
              <div className="overflow-hidden shadow-lg max-w-xs sm:max-w-sm mx-auto lg:mx-0">
                <img src={Person1} alt="Jaspal Singh - Director" className="w-full h-auto" />
              </div>
              {/* Name and Title below image */}
              <div className="text-center mt-3 sm:mt-4">
                <h6 className="text-lg sm:text-xl font-bold text-[#2F3C7E]">Jaspal Singh</h6>
                <p className="text-[#E6B325] font-semibold text-sm sm:text-base">Director</p>
              </div>
            </div>

            {/* Right Column - Content */}
            <div className="w-full lg:w-6/12 px-2 sm:px-4 md:px-6">
              <span className="text-xs sm:text-sm md:text-base font-semibold text-[#E6B325] uppercase tracking-wider block mb-3 sm:mb-4">
                About Us
              </span>

              <h5 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#2F3C7E] mb-4 sm:mb-6">
                A Legacy of Excellence
              </h5>

              <p className="text-sm sm:text-base text-gray-600 leading-relaxed sm:leading-loose">
                Our Director, Jaspal Singh, a visionary leader with an innate
                passion for hospitality, has epitomized the art of catering
                excellence since birth. With a rich heritage of family-owned
                hospitality ventures, he has imbibed the nuances of the industry
                from a tender age. He pursued Hotel Management studies in India,
                culminating in a PhD, and further honed his expertise through
                elite training stints in Dubai, London, Singapore, and Italy,
                mastering global hospitality standards.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* ===== SECTOR-SPECIFIC EXPERTISE SECTION ===== */}
      <section className="py-12 sm:py-16 md:py-20 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <h5 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#2F3C7E] mb-3 sm:mb-4">
              Sector-Specific Expertise
            </h5>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-3xl mx-auto px-4">
              Shivshakti Hospitality, spearheaded by Jaspal, has evolved into a multifaceted catering 
              powerhouse, catering to diverse sectors with precision and flair:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6 max-w-6xl mx-auto">
            {/* Industrial Catering */}
            <div className="bg-white p-4 sm:p-5 md:p-6 rounded-lg shadow-md hover:shadow-lg transition">
              <h6 className="text-base sm:text-lg md:text-xl font-bold text-[#2F3C7E] mb-2 sm:mb-3">Industrial Catering</h6>
              <p className="text-xs sm:text-sm text-gray-600">Tailored solutions for factory canteens, corporate offices, and construction sites</p>
            </div>

            {/* Corporate Hospitality */}
            <div className="bg-white p-4 sm:p-5 md:p-6 rounded-lg shadow-md hover:shadow-lg transition">
              <h6 className="text-base sm:text-lg md:text-xl font-bold text-[#2F3C7E] mb-2 sm:mb-3">Corporate Hospitality</h6>
              <p className="text-xs sm:text-sm text-gray-600">Luxurious dining experiences for executives and clients</p>
            </div>

            {/* Educational Institutions */}
            <div className="bg-white p-4 sm:p-5 md:p-6 rounded-lg shadow-md hover:shadow-lg transition">
              <h6 className="text-base sm:text-lg md:text-xl font-bold text-[#2F3C7E] mb-2 sm:mb-3">Educational Institutions</h6>
              <p className="text-xs sm:text-sm text-gray-600">Nutritious meals for schools and colleges</p>
            </div>

            {/* Healthcare Catering */}
            <div className="bg-white p-4 sm:p-5 md:p-6 rounded-lg shadow-md hover:shadow-lg transition">
              <h6 className="text-base sm:text-lg md:text-xl font-bold text-[#2F3C7E] mb-2 sm:mb-3">Healthcare Catering</h6>
              <p className="text-xs sm:text-sm text-gray-600">Specialized menus for hospitals and healthcare facilities</p>
            </div>

            {/* Temple and Community Catering */}
            <div className="bg-white p-4 sm:p-5 md:p-6 rounded-lg shadow-md hover:shadow-lg transition">
              <h6 className="text-base sm:text-lg md:text-xl font-bold text-[#2F3C7E] mb-2 sm:mb-3">Temple & Community Catering</h6>
              <p className="text-xs sm:text-sm text-gray-600">Authentic, hygienic offerings for sacred spaces</p>
            </div>

            {/* Event Management */}
            <div className="bg-white p-4 sm:p-5 md:p-6 rounded-lg shadow-md hover:shadow-lg transition">
              <h6 className="text-base sm:text-lg md:text-xl font-bold text-[#2F3C7E] mb-2 sm:mb-3">Event Management</h6>
              <p className="text-xs sm:text-sm text-gray-600">Flair and finesse for weddings, conferences, and galas</p>
            </div>

            {/* ODC (Outdoor Catering) */}
            <div className="bg-white p-4 sm:p-5 md:p-6 rounded-lg shadow-md hover:shadow-lg transition sm:col-span-2 lg:col-span-1">
              <h6 className="text-base sm:text-lg md:text-xl font-bold text-[#2F3C7E] mb-2 sm:mb-3">ODC (Outdoor Catering)</h6>
              <p className="text-xs sm:text-sm text-gray-600">Seamless, bespoke dining experiences anywhere, anytime</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== OUR PHILOSOPHY SECTION ===== */}
      <section className="py-12 sm:py-16 md:py-20 bg-[#2F3C7E]">
        <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-16 xl:px-24">
          <div className="flex justify-center">
            <div className="w-full max-w-7xl text-center">
              <h5 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-4 sm:mb-6">
                Our Philosophy
              </h5>
              <p className="text-sm sm:text-base md:text-lg text-white/90 leading-relaxed sm:leading-relaxed px-2 sm:px-4">
                At Shivshakti Hospitality, we marry tradition with innovation, ensuring every meal, 
                every event, and every interaction exceeds expectations. Our team is dedicated to 
                delivering excellence, driven by our unwavering commitment to quality, service, 
                and customer delight.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CORE VALUES SECTION ===== */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <h5 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#2F3C7E] mb-4">
              Core Values
            </h5>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6 md:gap-8 max-w-5xl mx-auto">
            {/* Excellence */}
            <div className="bg-blue-50 p-6 sm:p-7 md:p-8 rounded-lg shadow-md hover:shadow-lg transition text-center">
              <div className="flex justify-center mb-3 sm:mb-4">
                <Award className="w-14 h-14 sm:w-16 sm:h-16 text-[#E6B325]" strokeWidth={1.5} />
              </div>
              <h6 className="text-lg sm:text-xl font-bold text-[#2F3C7E] mb-2 sm:mb-3">Excellence</h6>
              <p className="text-xs sm:text-sm text-gray-600">Unwavering pursuit of perfection</p>
            </div>

            {/* Innovation */}
            <div className="bg-blue-50 p-6 sm:p-7 md:p-8 rounded-lg shadow-md hover:shadow-lg transition text-center">
              <div className="flex justify-center mb-3 sm:mb-4">
                <Lightbulb className="w-14 h-14 sm:w-16 sm:h-16 text-[#E6B325]" strokeWidth={1.5} />
              </div>
              <h6 className="text-lg sm:text-xl font-bold text-[#2F3C7E] mb-2 sm:mb-3">Innovation</h6>
              <p className="text-xs sm:text-sm text-gray-600">Pioneering solutions for diverse needs</p>
            </div>

            {/* Customer Centricity */}
            <div className="bg-blue-50 p-6 sm:p-7 md:p-8 rounded-lg shadow-md hover:shadow-lg transition text-center sm:col-span-2 md:col-span-1">
              <div className="flex justify-center mb-3 sm:mb-4">
                <Heart className="w-14 h-14 sm:w-16 sm:h-16 text-[#E6B325]" strokeWidth={1.5} />
              </div>
              <h6 className="text-lg sm:text-xl font-bold text-[#2F3C7E] mb-2 sm:mb-3">Customer Centricity</h6>
              <p className="text-xs sm:text-sm text-gray-600">Anticipating and exceeding expectations</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <CTASection />
    </Layout>
  );
};

export default AboutPage;